// Shared UI rules for the ABDM M1 (ABHA creation / verification) flows.

// ABDM M1 testing guidance: wait 60s before allowing a resend, and allow at most two resends.
export const OTP_RESEND_SECONDS = 60;
export const MAX_OTP_RESENDS = 2;

export const INVALID_OTP_MESSAGE = 'Please enter an valid OTP. Entered OTP is either invalid or incorrect';
export const MAX_RESEND_REACHED_MESSAGE = 'Maximum OTP resend limit reached. Please try again later.';
export const KYC_PENDING_MESSAGE = 'Complete your KYC through PHR app to download ABHA card.';

// Sandbox suffix; the ABDM flows in this app are wired to the sandbox (@sbx) everywhere.
export const ABHA_ADDRESS_SUFFIX = '@sbx';

const firstString = (...values) => values.find((value) => typeof value === 'string' && value.trim());

/**
 * Pull a human-readable message out of an ABDM / backend error.
 * ABDM returns several shapes: {error: {code, message}}, {error: "..."}, {message},
 * {details: [{message}]} and field maps such as {"loginId": "Invalid LoginId"}.
 */
export const extractAbhaErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data;
  if (data && typeof data === 'object') {
    const apiError = data.error;
    const fromKnownKeys = firstString(
      typeof apiError === 'string' ? apiError : apiError?.message,
      data.message,
      Array.isArray(data.details) ? data.details[0]?.message : undefined,
      data.detail
    );
    if (fromKnownKeys) return fromKnownKeys;
    const fieldMessage = Object.values(data).find((value) => typeof value === 'string' && value.trim());
    if (fieldMessage) return fieldMessage;
    if (apiError?.code) return apiError.code;
  }
  return firstString(error?.message) || fallback;
};

/** ABDM sometimes answers a wrong OTP with HTTP 200 and authResult "failed". */
export const isFailedAuthResult = (response) => String(response?.authResult || '').toLowerCase() === 'failed';

/**
 * Message for a failed OTP verification: the standard invalid-OTP text when the
 * failure is about the OTP itself, otherwise whatever ABDM reported.
 */
export const otpVerificationErrorMessage = (error) => {
  const message = extractAbhaErrorMessage(error, '');
  if (!message || /otp/i.test(message)) return INVALID_OTP_MESSAGE;
  return message;
};

/** "7249601484" -> "******1484" */
export const maskMobile = (mobile) => {
  const digits = String(mobile || '').replace(/\D/g, '');
  if (digits.length < 4) return digits;
  return `${'*'.repeat(Math.max(digits.length - 4, 0))}${digits.slice(-4)}`;
};

/**
 * Last digits of the masked mobile in ABDM's Aadhaar OTP message, e.g.
 * "OTP sent to Aadhaar registered mobile number ending with ******1484" -> "1484".
 */
export const extractMaskedMobileSuffix = (message) => {
  const match = String(message || '').match(/[*xX]{2,}\s*(\d{2,4})/);
  return match ? match[1] : '';
};

/**
 * Whether the communication mobile is the Aadhaar-linked one (and so already
 * verified by the Aadhaar OTP). Uses the masked number from the Aadhaar OTP
 * message, because ABDM echoes the typed mobile back in the enrolment profile
 * whether or not it is the Aadhaar-linked number. When the message carries no
 * masked number we return false so the communication mobile gets its own OTP.
 */
export const isAadhaarLinkedMobile = (mobile, aadhaarOtpMessage) => {
  const suffix = extractMaskedMobileSuffix(aadhaarOtpMessage);
  const digits = String(mobile || '').replace(/\D/g, '');
  return Boolean(suffix) && digits.length === 10 && digits.endsWith(suffix);
};

export const ABHA_ADDRESS_RULES = [
  { key: 'min', label: 'Minimum length - 8 characters', test: (v) => v.length >= 8 },
  { key: 'max', label: 'Maximum length - 18 characters', test: (v) => v.length <= 18 },
  {
    key: 'special',
    label: 'Special characters allowed - 1 dot (.) and/or 1 underscore (_)',
    test: (v) => /^[a-zA-Z0-9._]*$/.test(v) && (v.match(/\./g) || []).length <= 1 && (v.match(/_/g) || []).length <= 1
  },
  {
    key: 'position',
    label: 'Special character dot and underscore should be in between. Special characters cannot be in the beginning or at the end',
    test: (v) => !/^[._]/.test(v) && !/[._]$/.test(v) && !/[._]{2}/.test(v)
  },
  {
    key: 'alnum',
    label: 'Alphanumeric - only numbers, only letters or any combination of numbers and letters is allowed.',
    test: (v) => /[a-zA-Z0-9]/.test(v)
  }
];

/** Rules (from ABHA_ADDRESS_RULES) the given username violates. */
export const getAbhaAddressRuleViolations = (value) => {
  const username = String(value || '').trim().split('@')[0];
  return ABHA_ADDRESS_RULES.filter((rule) => !rule.test(username)).map((rule) => rule.key);
};

/** ABHA number entered with or without hyphens -> 14 digits, or '' when invalid. */
export const normalizeAbhaNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 14 ? digits : '';
};

/**
 * False only when the profile explicitly says it is not KYC verified
 * (self-declared ABHA address profiles). Unknown -> treated as verified so
 * existing Aadhaar-based flows are not blocked.
 */
export const isAbhaProfileKycVerified = (profile) => {
  if (!profile || typeof profile !== 'object') return true;
  if (profile.kycVerified === false || profile.kycVerified === 'false') return false;
  const kycStatus = profile.kycStatus || profile.kyc_status;
  if (typeof kycStatus === 'string' && kycStatus.trim()) {
    return kycStatus.trim().toUpperCase() === 'VERIFIED';
  }
  return true;
};

const readBlobText = (blob) => {
  if (typeof blob?.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

const CARD_EXTENSIONS = { 'application/pdf': 'pdf', 'image/png': 'png', 'image/jpeg': 'jpg', 'image/svg+xml': 'svg' };

/**
 * Save the ABHA card blob returned by the backend. A JSON body (an ABDM error
 * that came back with HTTP 200) is surfaced as an error instead of being
 * written out as a broken image file.
 */
export const saveAbhaCardBlob = async (blob, baseName = 'abha-card') => {
  const type = String(blob?.type || '').split(';')[0].trim().toLowerCase();
  if (!blob || blob.size === 0 || type.includes('json') || type.startsWith('text/')) {
    let message = 'ABHA card was not returned by ABDM';
    try {
      const parsed = JSON.parse(await readBlobText(blob));
      message = extractAbhaErrorMessage({ response: { data: parsed } }, message);
    } catch (parseError) {
      // keep the generic message
    }
    throw new Error(message);
  }
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${baseName}.${CARD_EXTENSIONS[type] || 'png'}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

/** Error message for a request made with responseType "blob" (the JSON error body arrives as a Blob). */
export const blobErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      return extractAbhaErrorMessage({ response: { data: JSON.parse(await readBlobText(data)) } }, fallback);
    } catch (parseError) {
      return fallback;
    }
  }
  return extractAbhaErrorMessage(error, fallback);
};

/**
 * Whether the typed communication mobile is already the ABHA's mobile.
 * ABDM may return the profile mobile in full or masked ("******1484");
 * a masked one is compared by its visible last digits. Unknown -> false,
 * so the number gets verified.
 */
export const isSameAbhaMobile = (typedMobile, profileMobile) => {
  const typed = String(typedMobile || '').replace(/\D/g, '').slice(-10);
  const profile = String(profileMobile || '');
  const visible = profile.replace(/\D/g, '');
  if (typed.length !== 10 || !visible) return false;
  if (/[*xX]/.test(profile)) return visible.length >= 4 && typed.endsWith(visible);
  return visible.slice(-10) === typed;
};

/** Shown where the ABHA mobile cannot be changed with an ABHA-address (PHR) login. */
export const PHR_MOBILE_UPDATE_MESSAGE =
  'To change the mobile number on the ABHA, verify with the ABHA number or Aadhaar.';

export const tenDigits = (value) => String(value ?? '').replace(/\D/g, '').slice(-10);

/**
 * A patient form opened from an ABHA verification (`session`) has had its
 * mobile changed from the one it opened with (`session.baselineContact`),
 * and the new number is not yet on the ABHA: it must be verified there first.
 */
export const abhaMobileUpdatePending = (session, contact) => {
  const mobile = tenDigits(contact);
  return Boolean(session) && mobile.length === 10 && mobile !== session.baselineContact && mobile !== tenDigits(session.mobile);
};
